// src/components/finance/FinanceHubView.jsx
//
// The Financial Wellbeing hub — partner-authored (Scotiabank / Scotia Wealth
// Management), age-tiered, CMS-fed. One component renders all three tiers:
// the member's ageTier picks the copy, the sections and the hero palette, and
// the library is filtered so a kid never sees the adult shelf.
//
// Content comes from `useFinanceResources` (global library + org overlay) and
// is grouped by `sectionsForTier`; this file is layout only. The partner's
// mark and disclaimer come from data/contentPartners.js.
//
// `tier` overrides the member's own tier (Kidz Corner passes 'kid'); `onBack`
// renders a back control for surfaces that host the hub as a sub-view.

import React, { useState } from 'react';
import { Play, FileText, Headphones, ExternalLink, X, ArrowLeft, Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import { useOrganization } from '../../context/OrganizationContext';
import { useFinanceResources } from '../../hooks/useFinanceResources';
import { resolveFinanceTier, sectionsForTier, isFinanceHubEnabled } from '../../data/financeHub';
import { getContentPartner } from '../../data/contentPartners';
import PartnerLockup from './PartnerLockup';
import CustomYouTubePlayer from '../../shared/CustomYouTubePlayer';

const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
    return (match && match[2].length === 11) ? match[2] : null;
};

const TYPE_ICON = { Video: Play, PDF: FileText, Audio: Headphones, Link: ExternalLink };

// Partner palette as inline styles (Tailwind cannot see a hex that lives in a
// data file). Absent a palette, callers fall back to the org brand classes.
const tileStyle = (accent) => (accent ? { backgroundColor: accent.light, color: accent.primary } : undefined);
const tileClass = (accent) => (accent ? '' : 'bg-brand-light text-brand-primary');

const ResourceRow = ({ item, onOpen, accent }) => {
    const Icon = TYPE_ICON[item.type] || ExternalLink;
    const isVideo = item.type === 'Video' && !!getYouTubeVideoId(item.url);
    return (
        <button
            type="button"
            onClick={() => onOpen(item)}
            className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-300 hover:bg-white transition-colors text-left"
        >
            <span
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${tileClass(accent)}`}
                style={tileStyle(accent)}
            >
                <Icon size={15} fill={isVideo ? 'currentColor' : 'none'} />
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-slate-800 leading-snug">{item.title}</span>
                {item.content && <span className="block text-xs text-slate-500 font-medium mt-0.5 line-clamp-2">{item.content}</span>}
                {(item.duration || item.isPrivate) && (
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">
                        {[item.duration, item.isPrivate && 'From your organization'].filter(Boolean).join(' · ')}
                    </span>
                )}
            </span>
        </button>
    );
};

const SectionCard = ({ section, onOpen, accent }) => {
    const Icon = section.icon;
    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
            <div className="flex items-start gap-4 mb-4">
                <div
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${tileClass(accent)}`}
                    style={tileStyle(accent)}
                >
                    <Icon size={22} />
                </div>
                <div className="min-w-0">
                    <h3 className="text-lg font-black text-slate-900 leading-tight">{section.title}</h3>
                    <p className="text-sm text-slate-500 font-medium">{section.hook}</p>
                </div>
            </div>
            <div className="space-y-2">
                {section.items.map((item) => <ResourceRow key={item.id} item={item} onOpen={onOpen} accent={accent} />)}
            </div>
        </div>
    );
};

const FinanceHubView = ({ currentUser, tier: tierOverride = null, onBack = null }) => {
    const { orgData } = useOrganization();
    const orgId = orgData?.id || currentUser?.organizationId || null;
    const tier = tierOverride || resolveFinanceTier(currentUser);
    const partner = getContentPartner('finance');
    const { resources, loaded } = useFinanceResources(orgId);
    const { config, sections, more, total } = sectionsForTier(tier, resources);
    const [activeResource, setActiveResource] = useState(null);

    // Partner colours. The adult hero wears them outright; younger tiers keep
    // their own palette and carry the partner in the tiles and lockup only.
    const accent = partner?.colors || null;
    const partnerHero = !!(config.partnerHero && accent);
    const heroClass = partnerHero ? '' : `bg-gradient-to-br ${config.heroClass}`;
    const heroStyle = partnerHero
        ? { backgroundImage: `linear-gradient(135deg, ${accent.primary} 0%, ${accent.dark} 100%)` }
        : undefined;

    const openResource = (item) => {
        if (item.type === 'Video' && getYouTubeVideoId(item.url)) setActiveResource(item);
        else if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer');
    };

    // Defensive: the nav and routers already gate on the org flag; this only
    // fires on a stale hash after an org turns the hub off.
    if (!isFinanceHubEnabled(orgData)) {
        return (
            <div className="max-w-2xl mx-auto py-16 text-center text-slate-400">
                <p className="text-sm font-medium">Financial Wellbeing is not enabled for your organization.</p>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in pb-20">

            {/* VIDEO MODAL — same treatment as Brain Health / Topic resources */}
            {activeResource && (
                <div className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setActiveResource(null)}>
                    <div className="bg-white w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl flex flex-col animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2 min-w-0">
                                <span className="p-2 bg-red-50 text-red-500 rounded-lg shrink-0"><Play size={16} fill="currentColor" /></span>
                                <span className="truncate">{activeResource.title}</span>
                            </h3>
                            <div className="flex items-center gap-1 shrink-0">
                                {activeResource.url && (
                                    <a href={activeResource.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-200 rounded-full transition-colors" title="Open on YouTube"><ExternalLink size={18} className="text-slate-500" /></a>
                                )}
                                <button type="button" onClick={() => setActiveResource(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors" aria-label="Close"><X size={20} className="text-slate-500" /></button>
                            </div>
                        </div>
                        <div className="w-full aspect-video bg-black">
                            <CustomYouTubePlayer videoId={getYouTubeVideoId(activeResource.url)} />
                        </div>
                    </div>
                </div>
            )}

            {/* HERO — tier palette; the partner mark sits here, before any content */}
            <div className={`${heroClass} rounded-3xl p-6 md:p-8 text-white relative overflow-hidden shadow-lg`} style={heroStyle}>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
                <div className="relative z-10">
                    {onBack && (
                        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-white/70 hover:text-white mb-4 transition-colors">
                            <ArrowLeft size={14} /> Back
                        </button>
                    )}
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div className="max-w-xl">
                            <div className="flex items-center gap-2 text-white/70 text-[11px] font-black uppercase tracking-widest mb-2">
                                <Sparkles size={14} /> Financial Wellbeing
                            </div>
                            <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">{config.title}</h1>
                            <p className="text-white/80 font-medium leading-relaxed">{config.intro}</p>
                        </div>
                        <div className="shrink-0 bg-white/15 border border-white/25 rounded-2xl px-4 py-3 backdrop-blur-sm">
                            <PartnerLockup partner={partner} onDark />
                        </div>
                    </div>
                </div>
            </div>

            {!loaded && (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-primary" size={32} /></div>
            )}

            {/* EMPTY — a launch-week state, not an error. The partner is credited
                in the hero and footer; content areas stay clean of logos. */}
            {loaded && total === 0 && (
                <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-10 text-center">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${tileClass(accent)}`} style={tileStyle(accent)}><Sparkles size={26} /></div>
                    <h3 className="text-lg font-black text-slate-900 mb-1">Content is on its way</h3>
                    <p className="text-sm text-slate-500 font-medium max-w-md mx-auto">
                        The first guides from {partner?.name || 'our partner'} are being prepared for this section. Check back soon.
                    </p>
                </div>
            )}

            {loaded && sections.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {sections.map((s) => <SectionCard key={s.tag} section={s} onOpen={openResource} accent={accent} />)}
                </div>
            )}

            {loaded && more.length > 0 && (
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4">
                        More resources
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {more.map((item) => <ResourceRow key={item.id} item={item} onOpen={openResource} accent={accent} />)}
                    </div>
                </div>
            )}

            {/* FOOTER — the disclaimer is the partner's, verbatim, on every tier */}
            {partner && (
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 flex flex-col md:flex-row md:items-center gap-4">
                    <PartnerLockup partner={partner} compact className="shrink-0" />
                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed flex items-start gap-1.5 md:border-l md:border-slate-200 md:pl-4">
                        <ShieldCheck size={13} className="shrink-0 mt-0.5 text-slate-400" />
                        <span>{partner.disclaimer}</span>
                    </p>
                </div>
            )}
        </div>
    );
};

export default FinanceHubView;
